import React from 'react';
import {
  formatMoney,
  formatMoneyAutoDecimal,
  NSB_SKY_REWARDS_LOZENGE_TEXT,
  SPLITIO_KEY,
  TANDC_URL,
  useSplitIO,
} from '@sky-tv-group/shared';
import { MarketingRectangle } from '../arrow-marketing/MarketingRectangle';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Coupon, ImageDetails } from '@sky-tv-group/graphql';
import { BLOCKS } from '@contentful/rich-text-types';

export interface GenericPromotionaCardDetails {
  bgColor?: string | null;
  billingFrequency?: string | null;
  campaign?: string | null;
  coupon?: Coupon | null;
  disclaimer?: string | null;
  discountText?: string | null;
  discountedPrice: number | null;
  fromPrice?: string | null;
  imageSrc?: ImageDetails | null;
  lozengeText?: string | null;
  sku?: string | null;
  occurrenceType?: string | null;
  subtitle?: string | null;
  subDescription?: any | null;
  termsAndConditions?: any | null;
  title: string;
  type?: string | null;
  copyrightText?: string | null;
  descriptionList?: string[] | null;
  iconList?: ImageDetails[] | null;
  addLink?: string | null;
  ctaName?: string | null;
}

export interface GenericPromotionalCardProps {
  card: GenericPromotionaCardDetails;
  buttons?: React.ReactNode;
  bgColor?: string;
  fixedWidth?: number;
  showBottomGradient?: boolean;
  cardType?: 'PRODUCT' | 'OFFER';
  compactMode?: boolean;
}

interface wrapperProps {
  fixedWidth?: number;
  children: React.ReactNode;
}

const GreenTick = ({ className }: { className?: string }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect x="0.479492" y="0.339844" width="19.1099" height="19.1099" rx="9.55494" fill="#8DC63F" />
    <path d="M5.57544 9.92974L8.56065 12.8351L14.1994 7.34717" stroke="white" strokeWidth="2.12332" />
  </svg>
);

function wrapDecimalInSpan(num: number): string {
  const [whole, decimal] = num.toFixed(2).split('.');
  return `${whole}<span class="text-body-xs" style="line-height: 0; margin-top: 19px;">.${decimal}</span>`;
}
export const contentfullOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => {
      return (
        <div style={{ marginBottom: '1px', whiteSpace: 'pre-line' }}>
          {children}
          <br />
        </div>
      );
    },
    [BLOCKS.UL_LIST]: (node: any, children: any) => (
      <ul className="list-disc list-outside ml-4">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (node: any, children: any) => (
      <ol className="list-decimal list-outside ml-4">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (node: any, children: any) => {
      return <li className="pl-1">{children}</li>;
    },
  },
};
export function GenericPromotionalCard({
  card,
  buttons,
  bgColor,
  compactMode,
  fixedWidth,
  cardType = 'OFFER',
}: GenericPromotionalCardProps) {
  const [showCompetitionFlag] = useSplitIO(SPLITIO_KEY.SKYWEB_SHOW_PROMO_COMPETITION);
  const showCompetition = showCompetitionFlag && card.lozengeText === NSB_SKY_REWARDS_LOZENGE_TEXT;
  return (
    <div
      className={`rounded-l  flex flex-col generic-promotional-card w-full relative ${compactMode ? 'min-height-compact' : 'min-height-container'}`}
      style={{
        width: fixedWidth ?? undefined,
        background: card.bgColor ?? bgColor,
      }}>
      {card.lozengeText ? (
        <MarketingRectangle
          left={0}
          top={0}
          containerClasses=""
          containerStyle={{ left: 20, top: -16 }}
          mRectTag={card.lozengeText}
          mRectColor={
            cardType === 'PRODUCT'
              ? card.occurrenceType === 'Data'
                ? 'radial-gradient(100% 100% at 0% 0%, #FDB71A 0%, #F26522 100%)'
                : 'radial-gradient(100% 100% at 0% 0%, #F26522 0%, #F96DB1 100%)'
              : '#9100D4'
          }
          mRectTagClasses="text-primary-white  text-center sky-h4-bold py-sky-xs pl-3 pr-4"
        />
      ) : null}
      <div className="pt-8 pl-5 pr-8 pb-2 flex flex-col gap-4 z-10 ">
        {card.occurrenceType === 'Data' && (
          <div className="flex items-center pt-3 text-primary-white">
            <img className="mr-2 mb-2" src={'https://static.sky.co.nz/sky/icons/icon-color-wifi.svg'} alt="" />
            <span className="sky-h5-black mr-2"> UNLIMITED </span> fibre
          </div>
        )}

        <h5 style={{ fontSize: '32px', lineHeight: '34px' }} className="font-skyBlack text-primary-white">
          {card.title}
        </h5>
        <p className="font-skyBook text-primary-white  whitespace-pre-line" style={{ fontSize: '14px', lineHeight: '18px' }}>
        {card.subtitle}
        {card.subDescription && documentToReactComponents(card.subDescription,contentfullOptions)}
        {showCompetition && (
            <>
              <br />
              <br />
              <b>And, be into WIN a new Sky Box plus a year's Sky subscription on us!</b>
            </>
          )}
        </p>
        <div className="text-primary-white">
          {card.fromPrice !== card.discountedPrice?.toString() &&
            (card.fromPrice ? (
              <p className="line-through " style={{ fontSize: '14px', lineHeight: '18px' }}>
                {formatMoney(+card.fromPrice)}
              </p>
            ) : (
              <p className=" " style={{ fontSize: '14px', lineHeight: '18px' }}>
                From
              </p>
            ))}
          {card.discountedPrice !== null ? (
            <div>
              <div className="ml-4 pr-2 mr-8 relative inline-block">
                <span
                  className="font-skyBlack absolute "
                  style={{ fontSize: 20, lineHeight: '24px', right: '100%', top: 6 }}>
                  $
                </span>
                <span className="font-skyBlack" style={{ fontSize: 46, lineHeight: '56px' }}>
                  <span
                    className="flex flex-row items-start"
                    dangerouslySetInnerHTML={{
                      __html: wrapDecimalInSpan(+formatMoneyAutoDecimal(card.discountedPrice).substring(1)),
                    }}
                  />
                </span>
                {card.billingFrequency === 'Monthly' && (
                  <span
                    className=" absolute  "
                    style={{ fontSize: 12, lineHeight: '16px', left: 'calc(100% - 25px)', bottom: 6 }}>
                    /mth
                  </span>
                )}
                {card.billingFrequency === 'One Time Payment' && (
                  <span
                    className="absolute min-w-28"
                    style={{ fontSize: 12, lineHeight: '16px', left: 'calc(100% - 33px)', bottom: 6 }}>
                    /one-off charge
                  </span>
                )}
                {card.discountText ? (
                  <MarketingRectangle
                    left={0}
                    top={0}
                    containerClasses=""
                    containerStyle={{ left: '100%', top: 6 }}
                    mRectTag={card.discountText}
                    mRectColor="#9100D4"
                    mRectTagClasses="text-primary-white  text-center sky-h7-bold pl-2 pr-4"
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {card.descriptionList && card.descriptionList.length && (
            <ul className="flex-grow mt-sky-xs">
              {card.descriptionList &&
                card.descriptionList.map((item: any, index: number) => {
                  return (
                    <li
                      style={{ paddingLeft: 20 }}
                      className={`relative text-left tick sky-h7 text-primary-white  text-[14.8px] mb-sky-xs ${card.type === 'Bundle' && (index == 1  || index == 3) ? 'font-skyBold' : ''}`}
                      key={`${index}-${item}`}>
                      <span className="absolute left-0" style={{ top: 2, width: 14, height: 14 }}>
                        <GreenTick className="mr-sky-sm w-full" />
                      </span>

                      {item}
                    </li>
                  );
                })}
            </ul>
          )}

          {card.iconList && card.iconList.length && !compactMode && (
            <div className="flex-grow flex flex-row flex-wrap align-middle items-center">
              {card.iconList?.map((item: any, index: number) => {
                return (
                  <div className="my-sky-sm mx-sky-xs flex align-content-center justify-center" key={index}>
                    <img
                      className="h-[30px] object-contain"
                      src={item?.url ? item.url : item.fields?.image?.fields?.file?.url}
                      alt={item?.text ? item.text : item.fields?.image?.fields?.file?.text}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {card.termsAndConditions ? (
            <p className="" style={{ fontSize: 10, lineHeight: '12px' }}>
              {documentToReactComponents(card.termsAndConditions)}
            </p>
          ) : null}
          {card.disclaimer ? (
            <p className="" style={{ fontSize: 6, lineHeight: '8px' }}>
              {card.disclaimer}
            </p>
          ) : null}
          {showCompetition && (
            <p className="" style={{ fontSize: 10, lineHeight: '12px' }}>
              Competition <a href={TANDC_URL}>T&Cs</a> apply.
            </p>
          )}
        </div>
      </div>

      {!compactMode && (
        <>
          {cardType !== 'PRODUCT' && (
            <div
              className="relative z-5 w-full"
              style={{
                background: `linear-gradient(to bottom, ${card.bgColor ?? bgColor} , rgba(0,0,0,0) 50%)`,
                height: 150,
                marginBottom: -150,
              }}></div>
          )}

          <div
            className={`w-full z-0 relative rounded-l overflow-hidden flex-1 ${
              cardType === 'PRODUCT' ? 'flex flex-col justify-center' : ''
            }`}
            style={{ minHeight: cardType === 'PRODUCT' ? '' : 280, marginTop: cardType === 'PRODUCT' ? -60 : '' }}>
            {cardType === 'PRODUCT' && (
              <div
                className="relative z-5 w-full"
                style={{
                  background: `linear-gradient(to bottom, ${card.bgColor ?? bgColor}, ${card.bgColor ??
                    bgColor}, rgba(0,0,0,0) 100%)`,
                  height: 150,
                  marginBottom: -130,
                }}></div>
            )}
            {card?.imageSrc?.url ? (
              <img
                src={card.imageSrc?.url}
                alt={card.imageSrc?.text ?? ''}
                style={{ minHeight: 300 }}
                className={`w-full object-center ${
                  cardType === 'PRODUCT' ? 'object-contain' : 'absolute left-0 top-0 h-full object-contain'
                }`}
              />
            ) : null}
            {cardType === 'PRODUCT' && (
              <div
                className="relative z-5 w-full"
                style={{
                  background: `linear-gradient(to top, ${card.bgColor ?? bgColor}, ${card.bgColor ??
                    bgColor}, rgba(0,0,0,0) 100%)`,
                  height: 150,
                  marginTop: -130,
                }}></div>
            )}
          </div>
        </>
      )}

      {buttons ? <div className={`${compactMode ? 'mt-auto' : 'absolute bottom-0'} w-full`}>{buttons}</div> : null}
      {card.copyrightText && (
        <span style={{ right: 30, bottom: 3, fontSize: 6 }} className="absolute sky-text-xxs text-primary-white">
          {card.copyrightText}
        </span>
      )}
    </div>
  );
}
